import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { cx } from "@/lib/utils";
import { readyFeaturePages } from "@/marketing/featurePages";

/**
 * Header „Co umíme" dropdown — the section's entry point in the nav.
 *
 * Platform shell only; ``useShellNav`` decides, and its
 * ``showFeaturesDropdown`` note says why a tenant storefront must never
 * render this.
 *
 * The entries come from ``FEATURE_PAGES`` rather than being typed here,
 * so the menu, the footer column and the ``/features`` hub cannot
 * disagree about a slug. Only pages that have a route render.
 *
 * Interaction is a deliberate copy of ``ServicesDropdown``: same toggle
 * button, same outside-click and Escape handling, same ``role="menu"``.
 * Two dropdowns in one header that dismiss differently is the kind of
 * difference nobody can name and everybody feels.
 */
export function FeaturesDropdown({
  lang,
  onNavigate,
}: {
  lang: string;
  onNavigate?: () => void;
}): JSX.Element | null {
  const { t } = useTranslation("saas");
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (!ref.current) return;
      if (!ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const items = readyFeaturePages();
  // Nothing shipped yet ⇒ no entry at all. A dropdown that opens onto
  // one link is worse than the link, and an empty one is a bug the
  // visitor sees.
  if (items.length < 2) return null;

  const close = () => {
    setOpen(false);
    onNavigate?.();
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        className={cx(
          "rounded-md px-2 py-1 text-sm transition-colors",
          open ? "font-medium text-ink" : "text-ink-2 hover:text-ink",
        )}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        data-testid="nav-features-toggle"
      >
        {t("features.hub.eyebrow")}
        <span aria-hidden className="ml-1 text-[10px]">▾</span>
      </button>
      {open && (
        <div
          role="menu"
          className="absolute left-0 z-30 mt-1 min-w-[16rem] rounded-md border border-border bg-surface p-1 shadow-md"
          data-testid="nav-features-menu"
        >
          {/* The hub first: it is the only page that shows the breadth,
              and somebody who opened this menu is asking exactly that. */}
          <Link
            role="menuitem"
            to={`/${lang}/features`}
            onClick={close}
            className="flex rounded px-3 py-2 text-sm text-ink transition hover:bg-surface-2"
            data-testid="nav-features-all"
          >
            {t("features.hub.overview")}
          </Link>
          {items.map((page) => (
            <Link
              key={page.testId}
              role="menuitem"
              to={`/${lang}${page.to}`}
              onClick={close}
              className="flex rounded px-3 py-2 text-sm text-ink transition hover:bg-surface-2"
              data-testid={`nav-${page.testId}`}
            >
              {t(`features.tiles.${page.i18nKey}.title`)}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
